import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { userProfileAPI } from '../../services/api';
import Swal from 'sweetalert2';
import './UserProfile.css';

const UserProfile = () => {
  const { t } = useTranslation(['userProfile', 'common']);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await userProfileAPI.getProfile();
      setProfile(data);
      setFormData({
        name: data.name || '',
        email: data.email || ''
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      Swal.fire(t('userProfile:messages.error'), t('userProfile:messages.loadProfileError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    try {
      await userProfileAPI.updateProfile(formData);
      await loadProfile();
      setEditMode(false);
      Swal.fire(t('userProfile:messages.success'), t('userProfile:messages.updateSuccess'), 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      const message = error.response?.data?.message || t('userProfile:messages.updateFailed');
      Swal.fire(t('userProfile:messages.error'), message, 'error');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      Swal.fire(t('userProfile:messages.error'), t('userProfile:validation.passwordMismatch'), 'error');
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 8) {
      Swal.fire(t('userProfile:messages.error'), t('userProfile:validation.passwordMin'), 'error');
      return;
    }

    // Validate password contains uppercase
    if (!/[A-Z]/.test(passwordData.newPassword)) {
      Swal.fire(t('userProfile:messages.error'), t('userProfile:validation.passwordUppercase'), 'error');
      return;
    }

    // Validate password contains lowercase
    if (!/[a-z]/.test(passwordData.newPassword)) {
      Swal.fire(t('userProfile:messages.error'), t('userProfile:validation.passwordLowercase'), 'error');
      return;
    }

    // Validate password contains number
    if (!/[0-9]/.test(passwordData.newPassword)) {
      Swal.fire(t('userProfile:messages.error'), t('userProfile:validation.passwordNumber'), 'error');
      return;
    }

    try {
      await userProfileAPI.changePassword(passwordData);
      setShowChangePassword(false);
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
      Swal.fire(t('userProfile:messages.success'), t('userProfile:messages.passwordChangeSuccess'), 'success');
    } catch (error) {
      console.error('Error changing password:', error);
      const message = error.response?.data?.message || t('userProfile:messages.passwordChangeFailed');
      Swal.fire(t('userProfile:messages.error'), message, 'error');
    }
  };

  const cancelEdit = () => {
    setFormData({
      name: profile?.name || '',
      email: profile?.email || ''
    });
    setEditMode(false);
  };

  const cancelPasswordChange = () => {
    setPasswordData({
      newPassword: '',
      confirmPassword: ''
    });
    setShowChangePassword(false);
  };

  if (loading) {
    return (
      <div className="user-profile-container">
        <div className="loading">{t('userProfile:loading')}</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="user-profile-container">
        <div className="error">{t('userProfile:loadingError')}</div>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="page-header">
        <h1>{t('userProfile:title')} ({t('userProfile:titleEn')})</h1>
      </div>

      <div className="profile-content">
        {/* Profile Information Card */}
        <div className="profile-card">
          <div className="card-header">
            <h2>{t('userProfile:profileInformation')}</h2>
            {!editMode && !showChangePassword && (
              <button className="btn btn-primary" onClick={() => setEditMode(true)}>
                <i className="fas fa-edit"></i> {t('userProfile:editProfile')}
              </button>
            )}
          </div>

          <div className="card-body">
            {editMode ? (
              <form onSubmit={handleUpdateProfile}>
                <div className="form-group">
                  <label>{t('userProfile:editMode.nameLabel')}</label>
                  <input
                    type="text"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('userProfile:editMode.emailLabel')}</label>
                  <input
                    type="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn btn-success">
                    <i className="fas fa-save"></i> {t('userProfile:editMode.saveChanges')}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={cancelEdit}>
                    <i className="fas fa-times"></i> {t('userProfile:editMode.cancel')}
                  </button>
                </div>
              </form>
            ) : (
              <div className="profile-info">
                <div className="info-row">
                  <label>{t('userProfile:viewMode.name')}</label>
                  <span>{profile.name}</span>
                </div>
                <div className="info-row">
                  <label>{t('userProfile:viewMode.email')}</label>
                  <span>{profile.email}</span>
                </div>
                <div className="info-row">
                  <label>{t('userProfile:viewMode.role')}</label>
                  <span className="badge">{profile.role}</span>
                </div>
                {profile.schoolCode && (
                  <>
                    <div className="info-row">
                      <label>{t('userProfile:viewMode.schoolCode')}</label>
                      <span>{profile.schoolCode}</span>
                    </div>
                    <div className="info-row">
                      <label>{t('userProfile:viewMode.schoolName')}</label>
                      <span>{profile.schoolName}</span>
                    </div>
                  </>
                )}
                {profile.ppdDistrict && (
                  <div className="info-row">
                    <label>{t('userProfile:viewMode.ppdDistrict')}</label>
                    <span>{profile.ppdDistrict}</span>
                  </div>
                )}
                {profile.dunName && (
                  <div className="info-row">
                    <label>{t('userProfile:viewMode.dun')}</label>
                    <span>{profile.dunCode} - {profile.dunName}</span>
                  </div>
                )}
                {profile.parName && (
                  <div className="info-row">
                    <label>{t('userProfile:viewMode.parliament')}</label>
                    <span>{profile.parCode} - {profile.parName}</span>
                  </div>
                )}
                <div className="info-row">
                  <label>{t('userProfile:viewMode.memberSince')}</label>
                  <span>{profile.createdDate ? new Date(profile.createdDate).toLocaleDateString() : 'N/A'}</span>
                </div>
                {profile.lastModifiedDate && (
                  <div className="info-row">
                    <label>{t('userProfile:viewMode.lastUpdated')}</label>
                    <span>{new Date(profile.lastModifiedDate).toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Change Password Card */}
        {!editMode && (
          <div className="profile-card">
            <div className="card-header">
              <h2>{t('userProfile:security')}</h2>
              {!showChangePassword && (
                <button className="btn btn-warning" onClick={() => setShowChangePassword(true)}>
                  <i className="fas fa-key"></i> {t('userProfile:changePassword')}
                </button>
              )}
            </div>

            <div className="card-body">
              {showChangePassword ? (
                <form onSubmit={handleChangePassword}>
                  <div className="form-group">
                    <label>{t('userProfile:passwordForm.newPasswordLabel')}</label>
                    <input
                      type="password"
                      name="newPassword"
                      className="form-control"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      required
                      minLength="8"
                    />
                    <small className="form-text">
                      {t('userProfile:passwordForm.requirements')}
                    </small>
                  </div>

                  <div className="form-group">
                    <label>{t('userProfile:passwordForm.confirmPasswordLabel')}</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      className="form-control"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      required
                    />
                  </div>

                  <div className="form-actions">
                    <button type="submit" className="btn btn-success">
                      <i className="fas fa-check"></i> {t('userProfile:passwordForm.submit')}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={cancelPasswordChange}>
                      <i className="fas fa-times"></i> {t('userProfile:passwordForm.cancel')}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="security-info">
                  <p>
                    <i className="fas fa-shield-alt"></i> {t('userProfile:passwordForm.securityMessage1')}
                  </p>
                  <p className="text-muted">
                    {t('userProfile:passwordForm.securityMessage2')}
                  </p>
                  <p className="text-muted">{t('userProfile:passwordForm.securityMessage3')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
